import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Star, ShoppingCart, ArrowRight } from 'lucide-react';
import { api } from '../../config/api';

const fallbackItems = [
  { menu_id: 1, item_name: 'Flame Grilled Chicken', description: 'Our signature dish — marinated 24hrs, fire-grilled to perfection', price: 15000, image_url: 'https://images.unsplash.com/photo-1598515214211-89d3c73ae83b?w=600&q=80', badge: '🔥 Best Seller' },
  { menu_id: 2, item_name: 'Whole Chicken', description: 'Full grilled chicken perfect for sharing, slow-cooked to perfection', price: 45000, image_url: 'https://images.unsplash.com/photo-1594221708779-94832f4320d1?w=600&q=80', badge: '🏆 Most Ordered' },
  { menu_id: 3, item_name: 'Beef Kebab', description: 'Succulent beef skewers with fresh vegetables and house sauce', price: 8000, image_url: 'https://images.unsplash.com/photo-1529193591184-b1d58069ecdd?w=600&q=80', badge: '⭐ Chef\'s Pick' },
  { menu_id: 7, item_name: 'Beef Burger', description: 'Juicy beef burger with fresh toppings and special sauce', price: 12000, image_url: 'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=600&q=80', badge: '✨ Popular Today' },
  { menu_id: 5, item_name: 'Fried Fish', description: 'Crispy fried fish with golden chips and tartar sauce', price: 18000, image_url: 'https://images.unsplash.com/photo-1580217593608-61931cefc821?w=600&q=80', badge: '🔥 Best Seller' },
  { menu_id: 4, item_name: 'Chicken Kebab', description: 'Marinated chicken skewers grilled over open flame', price: 7000, image_url: 'https://images.unsplash.com/photo-1603360946369-dc9bb6258143?w=600&q=80', badge: '⭐ Chef\'s Pick' },
];

const badges = ['🔥 Best Seller', '🏆 Most Ordered', '⭐ Chef\'s Pick', '✨ Popular Today'];
const alsoOrdered = [['Whole Chicken', 'Soda'], ['Beef Kebab', 'Fried Fish'], ['Soda', 'Fresh Juice']];

const BestSellers = () => {
  const [items, setItems] = useState(fallbackItems);

  useEffect(() => {
    const fetchBestSellers = async () => {
      try {
        const res = await api.get('/menu.php');
        if (res.data.success && res.data.data.length > 0) {
          const available = res.data.data.filter(i => i.is_available !== 0 && i.is_available !== '0');
          const source = available.length > 0 ? available : res.data.data;
          const sliced = source.slice(0, 9).map((item, i) => ({
            ...item,
            badge: badges[i % badges.length]
          }));
          setItems(sliced);
        }
      } catch (e) { /* use fallback */ }
    };
    fetchBestSellers();
  }, []);

  const addToCart = (item) => {
    const existingCart = JSON.parse(localStorage.getItem('cart') || '[]');
    const existingIndex = existingCart.findIndex(c => c.menu_id === item.menu_id);
    let updatedCart;
    if (existingIndex >= 0) {
      updatedCart = [...existingCart];
      updatedCart[existingIndex] = { ...updatedCart[existingIndex], quantity: updatedCart[existingIndex].quantity + 1 };
    } else {
      updatedCart = [...existingCart, { ...item, quantity: 1 }];
    }
    localStorage.setItem('cart', JSON.stringify(updatedCart));
    window.dispatchEvent(new Event('cartUpdated'));
  };

  return (
    <section className="section-padding" style={{ backgroundColor: '#faf8f5' }}>
      <div className="container">
        <div className="text-center mb-5">
          <span className="section-subtitle">Most Loved</span>
          <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '2.5rem', fontWeight: 600 }}>Best Sellers</h2>
          <div className="gold-divider-center"></div>
          <p style={{ fontSize: '1.05rem', color: '#777', maxWidth: '550px', margin: '16px auto 0' }}>
            Our most ordered dishes — tried, tested, and loved by hundreds
          </p>
        </div>

        <div className="row g-3">
          {items.map((item, index) => (
            <div key={item.menu_id || index} className="col-md-6 col-lg-4" style={{ transitionDelay: `${index * 0.1}s` }}>
              <div className="card h-100 hover-lift best-seller-card" style={{
                borderRadius: '16px', overflow: 'hidden',
                boxShadow: '0 4px 25px rgba(0,0,0,0.06)', position: 'relative'
              }}>
                <div className="img-zoom" style={{ position: 'relative' }}>
                  <img src={item.image_url} alt={item.item_name}
                    style={{ height: '220px', objectFit: 'cover', width: '100%' }} loading="lazy" />
                  <div className="best-seller-badge">{item.badge || badges[index % badges.length]}</div>
                </div>
                <div className="card-body p-4">
                  <h5 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 600, fontSize: '1.15rem', marginBottom: '6px' }}>
                    {item.item_name}
                  </h5>
                  <p style={{ fontSize: '0.85rem', color: '#888', lineHeight: 1.7, marginBottom: '12px' }}>
                    {item.description}
                  </p>
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <div className="d-flex gap-0">
                      {[...Array(5)].map((_, i) => <Star key={i} size={12} fill={i < 4 ? '#D4AF37' : 'none'} color="#D4AF37" />)}
                    </div>
                    <span style={{ color: '#999', fontSize: '0.75rem' }}>★ {Math.floor(Math.random() * 100 + 50)} orders this week</span>
                  </div>
                  <div className="also-ordered">
                    {(alsoOrdered[index % alsoOrdered.length]).map((name, i) => (
                      <span key={i} className="also-chip">{name}</span>
                    ))}
                  </div>
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <span style={{ color: '#4CAF50', fontWeight: 700, fontSize: '1.1rem', fontFamily: 'Playfair Display, serif' }}>
                      UGX {Number(item.price).toLocaleString()}
                    </span>
                    <button className="btn btn-green" style={{ padding: '8px 16px', fontSize: '0.75rem', borderRadius: '8px' }}
                      onClick={() => addToCart(item)}>
                      <ShoppingCart size={12} /> Add
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-5">
          <Link to="/menu" className="btn btn-outline-gold" style={{ padding: '14px 36px' }}>
            View Full Menu <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default BestSellers;
